// Custom React Hook for Scenario Management
// Provides scenario management functionality for all calculators

import { useState, useEffect } from 'react';
import ScenarioService, { createScenarioTemplate } from './ScenarioService.js';

export const useScenarioManagement = (calculatorType) => {
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [currentScenario, setCurrentScenario] = useState(null);

  // Load scenarios on mount
  useEffect(() => {
    try {
      const scenarios = ScenarioService.getScenariosForCalculator(calculatorType);
      setSavedScenarios(scenarios);
    } catch (error) {
      }
  }, [calculatorType]);

  // Save scenario
  const handleSaveScenario = (scenario, results = null) => {
    try {
      // If scenario is just inputs, create a full scenario object
      let scenarioToSave;
      if (scenario.inputs) {
        // scenario is already a full scenario object
        scenarioToSave = { ...scenario };
        // Use name from scenario object if available, otherwise use state
        if (!scenarioToSave.name) {
          scenarioToSave.name = scenarioName;
        }
        // Always create a new ID for new saves (unless it's an update)
        if (!scenarioToSave.id || scenarioToSave.id === '') {
          scenarioToSave.id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        }
      } else {
        // scenario is just inputs
        scenarioToSave = createScenarioTemplate(calculatorType, scenario, results);
        scenarioToSave.name = scenarioName;
      }
      
      const success = ScenarioService.saveScenario(scenarioToSave);
      if (success) {
        const updatedScenarios = ScenarioService.getScenariosForCalculator(calculatorType);
        setSavedScenarios(updatedScenarios);
        setShowSaveModal(false);
        setScenarioName('');
        setCurrentScenario(scenarioToSave);
        alert('Scenario saved successfully!');
      } else {
        alert('Failed to save scenario. Please try again.');
      }
    } catch (error) {
      alert('Error saving scenario. Please try again.');
    }
  };

  // Load scenario
  const handleLoadScenario = (scenario, loadInputsCallback) => {
    try {
      if (loadInputsCallback && typeof loadInputsCallback === 'function') {
        loadInputsCallback(scenario.inputs);
        setCurrentScenario(scenario);
        setShowLoadModal(false);
        alert(`Scenario "${scenario.name}" loaded successfully!`);
      } else {
        alert('Error loading scenario. Callback function not available.');
      }
    } catch (error) {
      alert('Error loading scenario. Please try again.');
    }
  };

  // Delete scenario
  const handleDeleteScenario = (scenarioId) => {
    try {
      if (window.confirm('Are you sure you want to delete this scenario?')) {
        const success = ScenarioService.deleteScenario(scenarioId);
        if (success) {
          const updatedScenarios = ScenarioService.getScenariosForCalculator(calculatorType);
          setSavedScenarios(updatedScenarios);
          alert('Scenario deleted successfully!');
        } else {
          alert('Failed to delete scenario. Please try again.');
        }
      }
    } catch (error) {
      alert('Error deleting scenario. Please try again.');
    }
  };

  // Export functions
  const handleExportJSON = () => {
    if (currentScenario) {
      ScenarioService.exportScenarioToJSON(currentScenario);
    } else {
      alert('No scenario to export. Please save a scenario first.');
    }
  };

  const handleExportCSV = () => {
    if (currentScenario) {
      ScenarioService.exportResultsToCSV(currentScenario);
    } else {
      alert('No scenario to export. Please save a scenario first.');
    }
  };

  const handleExportPDF = () => {
    if (currentScenario) {
      ScenarioService.exportSummaryToPDF(currentScenario);
    } else {
      alert('No scenario to export. Please save a scenario first.');
    }
  };

  // Show disclaimer
  const handleShowDisclaimer = () => {
    setShowDisclaimer(true);
  };

  // Accept disclaimer
  const handleAcceptDisclaimer = () => {
    ScenarioService.acceptDisclaimer();
    setShowDisclaimer(false);
  };

  // Decline disclaimer
  const handleDeclineDisclaimer = () => {
    setShowDisclaimer(false);
  };

  return {
    // State
    savedScenarios,
    showSaveModal,
    showLoadModal,
    showExportModal,
    showDisclaimer,
    scenarioName,
    currentScenario,

    // State setters
    setShowSaveModal,
    setShowLoadModal,
    setShowExportModal,
    setShowDisclaimer,
    setScenarioName,
    setCurrentScenario,

    // Handlers
    handleSaveScenario,
    handleLoadScenario,
    handleDeleteScenario,
    handleExportJSON,
    handleExportCSV,
    handleExportPDF,
    handleShowDisclaimer,
    handleAcceptDisclaimer,
    handleDeclineDisclaimer
  };
};
